import './ClassBranchesImport.scss'

import { UploadOutlined } from '@ant-design/icons'
import { Alert, Button, Card, Col, List, notification, Row, Space, Table, Typography, Upload } from 'antd'
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import * as XLSX from 'xlsx'

import {
    SinifSubeCreateInput,
    SinifSubeUpdateInput,
    useSinifSubeTopluEkleMutation,
    useSinifSubeTopluGuncelleMutation,
} from '../../../../../gql/GeneratedGraphqlTypes'

const { Title, Text } = Typography

const EXPECTED_COLUMNS = ['id', 'isim', 'kampus_id', 'ust_kategori_id']

const ClassBranchesImport: React.FC = () => {
    const [file, setFile] = useState<File | null>(null)
    const [tableData, setTableData] = useState<any[]>([])
    const [columns, setColumns] = useState<any[]>([])
    const [errorMessages, setErrorMessages] = useState<Record<string, string>>({})
    const [isSubmitting, setIsSubmitting] = useState(false)

    const navigate = useNavigate()
    const [sinifSubeTopluEkle] = useSinifSubeTopluEkleMutation()
    const [sinifSubeTopluGuncelle] = useSinifSubeTopluGuncelleMutation()

    const handleFileChange = (info: any) => {
        const file = info.fileList.length > 0 ? info.fileList[0].originFileObj : null

        if (file) {
            setFile(file)
            readExcel(file)
        } else {
            setFile(null)
            setTableData([])
            setColumns([])
            setErrorMessages({})
        }
    }

    const handleRemove = () => {
        if (isSubmitting) return

        setFile(null)
        setTableData([])
        setColumns([])
        setErrorMessages({})
    }

    const readExcel = (file: File) => {
        const reader = new FileReader()
        reader.readAsBinaryString(file)
        reader.onload = (event) => {
            try {
                const data = event.target?.result
                const workbook = XLSX.read(data, { type: 'binary' })
                const sheetName = workbook.SheetNames[0]
                const sheet = workbook.Sheets[sheetName]

                const jsonData: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1 })

                if (!jsonData.length) {
                    notification.error({
                        message: 'Dosya boş veya okunamıyor.',
                    })
                    return
                }

                const [headersRaw, ...rows] = jsonData
                const headers = headersRaw.map((h) => String(h).trim())

                if (!headers.length || rows.length === 0) {
                    notification.error({ message: 'Geçersiz dosya formatı' })
                    return
                }

                const validHeaders = headers.filter((header) => EXPECTED_COLUMNS.includes(header))
                const nonEmptyRows = rows.filter((row) =>
                    row.some((cell) => cell !== null && cell !== undefined && String(cell).trim() !== '')
                )

                const newErrorMessages: Record<number, string> = {}
                const validRows: any[] = []

                nonEmptyRows.forEach((row, rowIndex) => {
                    const formattedRow: Record<string, any> = {}
                    validHeaders.forEach((header) => {
                        const originalIndex = headers.indexOf(header)
                        formattedRow[header] = row[originalIndex]
                    })

                    const errors = []
                    const hasId =
                        formattedRow['id'] !== undefined &&
                        formattedRow['id'] !== null &&
                        String(formattedRow['id']).trim() !== ''

                    if (!hasId) {
                        if (!formattedRow['kampus_id'] || isNaN(Number(formattedRow['kampus_id']))) {
                            errors.push('Kampus ID eksik veya geçersiz')
                        }
                    }

                    if (errors.length > 0) {
                        newErrorMessages[rowIndex] = `Satır ${rowIndex + 2}: ${errors.join(', ')}`
                    } else {
                        validRows.push(formattedRow)
                    }
                })

                if (Object.keys(newErrorMessages).length > 0) {
                    setErrorMessages(newErrorMessages)
                    setTableData([])
                    setColumns([])
                    notification.error({
                        message: 'Hatalı satırlar bulundu. Lütfen düzeltin.',
                    })
                    return
                }

                setErrorMessages({})

                const tableColumns = validHeaders.map((header, index) => ({
                    title: header,
                    dataIndex: `col${index}`,
                    key: `col${index}`,
                }))

                const tableRows = validRows.map((row, rowIndex) => {
                    const rowData: Record<string, any> = { key: rowIndex }
                    validHeaders.forEach((header, colIndex) => {
                        rowData[`col${colIndex}`] = row[header]
                    })
                    return rowData
                })

                setColumns(tableColumns)
                setTableData(tableRows)
            } catch (error) {
                notification.error({
                    message: 'Dosya okunurken hata oluştu.',
                })
            }
        }
    }

    const handleSubmit = async () => {
        if (isSubmitting) return

        if (!file) {
            notification.error({ message: 'Lütfen bir dosya seçin!' })
            return
        }

        if (tableData.length === 0) {
            notification.error({ message: 'Tabloda veri bulunmuyor!' })
            return
        }

        setIsSubmitting(true)

        try {
            const toInt = (v: any): number | undefined => {
                try {
                    const s = v === null || v === undefined ? '' : String(v).trim()
                    if (!s) return undefined
                    const n = Number(s)
                    if (!Number.isFinite(n)) return undefined
                    return Math.trunc(n)
                } catch {
                    return undefined
                }
            }

            const toStr = (v: any): string | undefined => {
                const s = v === null || v === undefined ? '' : String(v).trim()
                return s ? s : undefined
            }

            const getCell = (row: any, title: string) => {
                const idx = columns.findIndex((c) => c.title === title)
                if (idx < 0) return undefined
                return row[`col${idx}`]
            }

            const createPayload: SinifSubeCreateInput[] = []
            const updatePayload: SinifSubeUpdateInput[] = []
            const submitErrors: string[] = []

            tableData.forEach((row, i) => {
                const lineNo = i + 2

                const id = toInt(getCell(row, 'id'))
                const isim = toStr(getCell(row, 'isim'))
                const subeId = toInt(getCell(row, 'kampus_id'))
                const ustKat = toInt(getCell(row, 'ust_kategori_id'))

                if (id && id > 0) {
                    const u: SinifSubeUpdateInput = {
                        id,
                        ...(isim !== undefined ? { isim } : {}),
                        ...(subeId !== undefined ? { sube_id: subeId } : {}),
                        ...(ustKat !== undefined ? { ust_kategori_id: ustKat } : {}),
                    }

                    const hasAnyField =
                        u.isim !== undefined ||
                        u.sube_id !== undefined ||
                        u.ust_kategori_id !== undefined

                    if (!hasAnyField) {
                        submitErrors.push(`Satır ${lineNo}: ID var ama güncellenecek alan yok.`)
                        return
                    }

                    updatePayload.push(u)
                    return
                }

                if (!isim) submitErrors.push(`Satır ${lineNo}: isim zorunlu.`)
                if (!subeId || subeId <= 0) {
                    submitErrors.push(`Satır ${lineNo}: kampus_id zorunlu ve sayı olmalı.`)
                }

                if (isim && subeId && subeId > 0) {
                    const c: SinifSubeCreateInput = {
                        isim,
                        sube_id: subeId,
                        ...(ustKat !== undefined ? { ust_kategori_id: ustKat } : {}),
                    }
                    createPayload.push(c)
                }
            })

            if (submitErrors.length > 0) {
                notification.error({
                    message: 'Hatalı satırlar bulundu',
                    description:
                        submitErrors.slice(0, 5).join(' | ') + (submitErrors.length > 5 ? ' ...' : ''),
                    duration: 6,
                })
                return
            }

            if (createPayload.length === 0 && updatePayload.length === 0) {
                notification.warning({ message: 'Gönderilecek kayıt bulunamadı.' })
                return
            }

            if (updatePayload.length > 0) {
                const { data, errors } = await sinifSubeTopluGuncelle({
                    variables: { input: updatePayload },
                })

                if (errors?.length) {
                    notification.error({
                        message: 'Güncelleme Hatası',
                        description: errors[0]?.message || 'Güncelleme sırasında hata oluştu.',
                    })
                    return
                }

                if (!data?.sinifSubeTopluGuncelle?.success) {
                    notification.error({
                        message: 'Güncelleme Başarısız',
                        description: data?.sinifSubeTopluGuncelle?.message || 'Bilinmeyen hata',
                    })
                    return
                }
            }

            if (createPayload.length > 0) {
                const { data, errors } = await sinifSubeTopluEkle({
                    variables: { input: createPayload },
                })

                if (errors?.length) {
                    notification.error({
                        message: 'Ekleme Hatası',
                        description: errors[0]?.message || 'Ekleme sırasında hata oluştu.',
                    })
                    return
                }

                if (!data?.sinifSubeTopluEkle?.success) {
                    notification.error({
                        message: 'Ekleme Başarısız',
                        description: data?.sinifSubeTopluEkle?.message || 'Bilinmeyen hata',
                    })
                    return
                }
            }

            notification.success({
                message: 'İşlem tamamlandı',
                description: `Güncellenecek: ${updatePayload.length}, Eklenecek: ${createPayload.length}`,
                duration: 6,
            })

            navigate(-1)
        } catch (err: any) {
            notification.error({
                message: 'Bağlantı Hatası',
                description: err?.message || 'Sunucuya bağlanırken hata oluştu.',
            })
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <div className="okul-siparis-import-page">
            <Card title="Sınıf Kategorileri İçe Aktar" bordered={false}>
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    <Text>
                        Bu içe aktarıcı ilgili alanları içe aktaracaktır:{' '}
                        <Text code>{EXPECTED_COLUMNS.join(', ')}</Text>
                    </Text>

                    <Row>
                        <Col span={4}>
                            <Upload
                                beforeUpload={() => false}
                                onChange={handleFileChange}
                                fileList={file ? [{ uid: '-1', name: file.name, status: 'done' }] : []}
                                onRemove={handleRemove}
                                showUploadList={{ showRemoveIcon: !isSubmitting }}
                                disabled={isSubmitting}
                            >
                                <Button icon={<UploadOutlined />} disabled={isSubmitting}>
                                    Dosya Seç
                                </Button>
                            </Upload>
                        </Col>
                    </Row>

                    {tableData.length > 0 && (
                        <>
                            <Title level={5}>Önizleme:</Title>
                            <Table columns={columns} dataSource={tableData} pagination={{ pageSize: 5 }} />
                        </>
                    )}

                    {Object.keys(errorMessages).length > 0 && (
                        <List
                            header={<Title level={5}>Hatalar</Title>}
                            bordered
                            dataSource={Object.values(errorMessages)}
                            renderItem={(item) => (
                                <List.Item>
                                    <Alert message={item} type="error" showIcon />
                                </List.Item>
                            )}
                        />
                    )}

                    <div className="okul-yonetim-footer-buttons">
                        <Button className="back-btn" onClick={() => navigate(-1)} disabled={isSubmitting}>
                            Geri
                        </Button>
                        <Button
                            className="update-btn"
                            type="primary"
                            onClick={handleSubmit}
                            loading={isSubmitting}
                            disabled={!file || Object.keys(errorMessages).length > 0 || isSubmitting}
                        >
                            Kaydet
                        </Button>
                    </div>
                </Space>
            </Card>
        </div>
    )
}

export default ClassBranchesImport